<template>
  <div class="robot-teaching-records">
    <el-breadcrumb separator-class="el-icon-arrow-right">
      <el-breadcrumb-item :to="{ path: '/menu/welcome' }">首页</el-breadcrumb-item>
      <el-breadcrumb-item>用户管理</el-breadcrumb-item>
      <el-breadcrumb-item>机器人带教记录</el-breadcrumb-item>
    </el-breadcrumb>

    <el-card class="records-card">
      <el-form :inline="true" :model="filters" size="small" @submit.native.prevent="search">
        <el-form-item label="创建日期">
          <el-date-picker
            v-model="filters.dateRange"
            type="daterange"
            range-separator="至"
            start-placeholder="开始日期"
            end-placeholder="结束日期"
            :editable="false"
          />
        </el-form-item>
        <el-form-item label="会话状态">
          <el-select v-model="filters.status" clearable placeholder="全部状态">
            <el-option v-for="item in statuses" :key="item.value" :label="item.label" :value="item.value" />
          </el-select>
        </el-form-item>
        <el-form-item label="带教人姓名">
          <el-input v-model="filters.instructorName" clearable maxlength="64" placeholder="请输入带教人姓名" />
        </el-form-item>
        <el-form-item label="医生账号 ID">
          <el-input v-model="filters.therapistId" clearable maxlength="64" placeholder="请输入医生账号 ID" />
        </el-form-item>
        <el-form-item>
          <el-button type="primary" native-type="submit">查询</el-button>
          <el-button @click="reset">重置</el-button>
          <el-button :loading="loading" @click="loadList">刷新</el-button>
        </el-form-item>
      </el-form>
      <p class="muted">创建日期包含所选结束日，按本地时区筛选。时长和超时为接口快照，可手动刷新。</p>
      <div v-if="listError" class="error-state" role="alert">
        <span>{{ listError }}</span>
        <el-button size="small" @click="loadList">重试</el-button>
      </div>
      <el-table
        v-loading="loading"
        :data="rows"
        row-key="sessionId"
        border
        stripe
        :empty-text="loading ? '正在加载带教记录…' : (listError ? '记录加载失败，请重试' : '暂无符合条件的带教记录')"
      >
        <el-table-column label="创建时间" min-width="175">
          <template slot-scope="scope">{{ formatTime(scope.row.createdAt) }}</template>
        </el-table-column>
        <el-table-column label="带教人姓名" min-width="130">
          <template slot-scope="scope">{{ recorded(scope.row.instructorName) }}</template>
        </el-table-column>
        <el-table-column label="医生账号 ID" min-width="135">
          <template slot-scope="scope">{{ recorded(scope.row.therapistId) }}</template>
        </el-table-column>
        <el-table-column label="会话状态" min-width="145">
          <template slot-scope="scope">{{ statusLabel(scope.row.status) }}</template>
        </el-table-column>
        <el-table-column label="机器人数量" min-width="100">
          <template slot-scope="scope">{{ recorded(scope.row.robotCount) }}</template>
        </el-table-column>
        <el-table-column label="跳过数量" min-width="90">
          <template slot-scope="scope">{{ recorded(scope.row.skippedCount) }}</template>
        </el-table-column>
        <el-table-column label="时长（快照）" min-width="145">
          <template slot-scope="scope">{{ duration(scope.row, 'elapsedSeconds') }}</template>
        </el-table-column>
        <el-table-column label="超时（快照）" min-width="145">
          <template slot-scope="scope">{{ duration(scope.row, 'overtimeSeconds') }}</template>
        </el-table-column>
        <el-table-column label="操作" width="100" fixed="right">
          <template slot-scope="scope">
            <el-button type="text" size="small" @click="openDetail(scope.row)">查看详情</el-button>
          </template>
        </el-table-column>
      </el-table>
      <el-pagination
        class="pager"
        :current-page="page"
        :page-size="size"
        :page-sizes="[20, 50, 100]"
        :total="total"
        layout="total, sizes, prev, pager, next"
        @current-change="changePage"
        @size-change="changeSize"
      />
    </el-card>

    <el-drawer
      title="机器人带教记录详情"
      :visible.sync="detailVisible"
      size="85%"
      append-to-body
      @close="closeDetail"
    >
      <div v-loading="detailLoading" class="teaching-detail">
        <div class="detail-toolbar">
          <span class="muted">时长为接口快照，不自动计时或轮询。</span>
          <el-button size="small" :loading="detailLoading" @click="loadDetail">刷新详情</el-button>
        </div>
        <div v-if="detailError" class="error-state" role="alert">
          <span>{{ detailError }}</span>
          <el-button size="small" @click="loadDetail">重试</el-button>
        </div>
        <template v-if="detail">
          <h3>会话信息</h3>
          <dl class="session-fields">
            <div><dt>会话 ID</dt><dd>{{ recorded(detail.sessionId) }}</dd></div>
            <div><dt>带教人姓名</dt><dd>{{ recorded(detail.instructorName) }}</dd></div>
            <div><dt>医生账号 ID</dt><dd>{{ recorded(detail.therapistId) }}</dd></div>
            <div><dt>会话状态</dt><dd>{{ statusLabel(detail.status) }}</dd></div>
            <div><dt>创建时间</dt><dd>{{ formatTime(detail.createdAt) }}</dd></div>
            <div><dt>开始时间</dt><dd>{{ hasTimestamp(detail.startedAt) ? formatTime(detail.startedAt) : '未开始计时' }}</dd></div>
            <div><dt>进入结束阶段时间</dt><dd>{{ formatTime(detail.endingAt) }}</dd></div>
            <div><dt>结束时间</dt><dd>{{ formatTime(detail.endedAt) }}</dd></div>
            <div><dt>服务器快照时间</dt><dd>{{ formatTime(detail.serverTime) }}</dd></div>
            <div><dt>时长（快照）</dt><dd>{{ duration(detail, 'elapsedSeconds') }}</dd></div>
            <div><dt>超时（快照）</dt><dd>{{ duration(detail, 'overtimeSeconds') }}</dd></div>
            <div><dt>机器人 / 跳过数量</dt><dd>{{ recorded(detail.robotCount) }} / {{ recorded(detail.skippedCount) }}</dd></div>
          </dl>
          <h3>机器人明细（{{ detail.members.length }} 台）</h3>
          <p v-if="!detail.members.length" class="empty-state">暂无机器人明细</p>
          <div v-else class="member-grid">
            <el-card v-for="(member, index) in detail.members" :key="index" shadow="never">
              <div slot="header" class="member-title">{{ index + 1 }}. {{ recorded(member.robotName) }}</div>
              <dl class="member-fields">
                <div><dt>机器人名称</dt><dd>{{ recorded(member.robotName) }}</dd></div>
                <div><dt>机器人序列号</dt><dd>{{ recorded(member.robotSerial) }}</dd></div>
                <div><dt>机器人 ID</dt><dd>{{ recorded(member.robotId) }}</dd></div>
                <div><dt>床位号</dt><dd>{{ recorded(member.bedNo, '床位号未配置') }}</dd></div>
                <div><dt>位置名称</dt><dd>{{ recorded(member.positionName) }}</dd></div>
                <div><dt>患者姓名</dt><dd>{{ recorded(member.patientName) }}</dd></div>
                <div><dt>患者 ID</dt><dd>{{ recorded(member.patientId) }}</dd></div>
                <div><dt>训练方案名称</dt><dd>{{ recorded(member.planName) }}</dd></div>
                <div><dt>训练方案 ID</dt><dd>{{ recorded(member.trainingPlanId) }}</dd></div>
                <div><dt>是否跳过</dt><dd>{{ member.skipped === true ? '是' : (member.skipped === false ? '否' : '未记录') }}</dd></div>
                <div><dt>{{ terminal(detail.status) ? '最终状态' : '快照状态' }}</dt><dd>{{ statusLabel(member.status) }}</dd></div>
              </dl>
            </el-card>
          </div>
        </template>
        <p v-else-if="!detailLoading && !detailError" class="empty-state">暂无会话详情</p>
        <p v-if="detailLoading" class="muted" role="status">正在加载会话详情…</p>
      </div>
    </el-drawer>
  </div>
</template>

<script>
import { DatePicker } from 'element-ui'

const statuses = [
  { value: 'WAITING_FOR_JOIN', label: '等待加入' },
  { value: 'DISPATCHING', label: '调度中' },
  { value: 'RUNNING', label: '带教中' },
  { value: 'DOCTOR_DISCONNECTED', label: '医生连接中断' },
  { value: 'ENDING', label: '结束中' },
  { value: 'ENDED', label: '已结束' },
  { value: 'CANCELLED', label: '已取消' },
  { value: 'ENDED_UNCONFIRMED', label: '已结束（未确认）' }
]
const memberStatuses = [
  { value: 'DISPATCHED', label: '已派遣' },
  { value: 'RETURNING_HOME', label: '回桩中' },
  { value: 'HOME', label: '已回桩' },
  { value: 'RETURN_FAILED', label: '回桩失败' },
  { value: 'NAVIGATING', label: '导航中' },
  { value: 'ARRIVED', label: '已到达' },
  { value: 'JOINED', label: '已加入' },
  { value: 'NAVIGATION_FAILED', label: '导航失败' },
  { value: 'JOIN_FAILED', label: '入会失败' },
  { value: 'SKIPPED', label: '已跳过' },
  { value: 'RELEASED_UNCONFIRMED', label: '已释放（未确认）' }
]
const emptyFilters = () => ({ dateRange: [], status: '', instructorName: '', therapistId: '' })
const pageSize = value => Math.min(100, Math.max(1, Math.floor(Number(value)) || 20))

export default {
  name: 'RobotTeachingRecords',
  components: { ElDatePicker: DatePicker },
  data () {
    return {
      statuses,
      filters: emptyFilters(),
      appliedFilters: {},
      rows: [],
      page: 1,
      size: 20,
      total: 0,
      loading: false,
      listError: '',
      listRequestSequence: 0,
      detailVisible: false,
      detailLoading: false,
      detailError: '',
      detail: null,
      detailSessionId: null,
      detailRequestSequence: 0
    }
  },
  created () {
    this.loadList()
  },
  beforeDestroy () {
    this.listRequestSequence++
    this.detailRequestSequence++
  },
  methods: {
    terminal (status) {
      return ['ENDED', 'CANCELLED', 'ENDED_UNCONFIRMED'].includes(status)
    },
    hasValue (value) {
      return value !== null && value !== undefined && String(value).trim() !== ''
    },
    hasTimestamp (value) {
      return Number.isFinite(Number(value)) && Number(value) > 0
    },
    recorded (value, fallback = '未记录') {
      return this.hasValue(value) ? value : fallback
    },
    statusLabel (value) {
      const status = statuses.find(item => item.value === value) || memberStatuses.find(item => item.value === value)
      return status ? status.label : this.recorded(value)
    },
    formatTime (value) {
      if (!this.hasTimestamp(value)) return '未记录'
      const date = new Date(Number(value))
      if (Number.isNaN(date.getTime())) return '未记录'
      const pad = number => String(number).padStart(2, '0')
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
    },
    duration (row, field) {
      if (!this.hasTimestamp(row.startedAt)) return '未开始计时'
      if (!this.hasValue(row[field])) return '未记录'
      const seconds = Number(row[field])
      if (!Number.isFinite(seconds) || seconds < 0) return '未记录'
      const value = Math.floor(seconds)
      return `${Math.floor(value / 3600)}小时 ${Math.floor(value % 3600 / 60)}分 ${value % 60}秒`
    },
    search () {
      const params = {}
      const range = this.filters.dateRange
      if (range && range.length === 2) {
        const start = new Date(range[0])
        const end = new Date(range[1])
        start.setHours(0, 0, 0, 0)
        end.setHours(0, 0, 0, 0)
        if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || start > end) {
          this.$message.warning('请选择有效的创建日期范围')
          return
        }
        // 使用本地日历的次日零点，不能加固定 24 小时，避免夏令时边界偏移。
        const exclusiveEnd = new Date(end.getFullYear(), end.getMonth(), end.getDate() + 1)
        params.from = start.getTime()
        params.to = exclusiveEnd.getTime()
      }
      if (this.filters.status) params.status = this.filters.status
      const instructorName = this.filters.instructorName.trim()
      const therapistId = this.filters.therapistId.trim()
      if (instructorName) params.instructorName = instructorName
      // ID 保持字符串，避免大整数在 Number 转换后损失精度。
      if (therapistId) params.therapistId = therapistId
      this.appliedFilters = params
      this.page = 1
      this.loadList()
    },
    reset () {
      this.filters = emptyFilters()
      this.search()
    },
    changePage (page) {
      if (this.page === page) return
      this.page = Math.max(1, page)
      this.loadList()
    },
    changeSize (size) {
      this.size = pageSize(size)
      this.page = 1
      this.loadList()
    },
    async loadList () {
      const sequence = ++this.listRequestSequence
      const params = { ...this.appliedFilters, page: this.page, size: pageSize(this.size) }
      this.loading = true
      this.listError = ''
      this.rows = []
      try {
        const { data: resp } = await this.$http.get('cms/robot-teaching/records', { params })
        if (sequence !== this.listRequestSequence) return
        if (!resp || resp.code !== 200 || !resp.data || !Array.isArray(resp.data.rows)) {
          this.listError = this.responseMessage(resp, '带教记录加载失败，请重试')
          return
        }
        this.rows = resp.data.rows
        this.total = Math.max(0, Number(resp.data.total) || 0)
        this.page = Math.max(1, Math.floor(Number(resp.data.page)) || params.page)
        this.size = pageSize(resp.data.size || params.size)
      } catch (error) {
        if (sequence !== this.listRequestSequence) return
        const response = error.response
        this.listError = this.responseMessage(response && response.data, '带教记录加载失败，请重试', response && response.status)
      } finally {
        if (sequence === this.listRequestSequence) this.loading = false
      }
    },
    openDetail (row) {
      this.detailSessionId = row.sessionId
      this.detailVisible = true
      this.loadDetail()
    },
    closeDetail () {
      this.detailRequestSequence++
      this.detailLoading = false
      this.detail = null
      this.detailError = ''
      this.detailSessionId = null
    },
    async loadDetail () {
      if (!this.detailVisible || !this.hasValue(this.detailSessionId)) return
      const sequence = ++this.detailRequestSequence
      const sessionId = this.detailSessionId
      this.detailLoading = true
      this.detailError = ''
      this.detail = null
      try {
        const { data: resp } = await this.$http.get(`cms/robot-teaching/records/${encodeURIComponent(sessionId)}`)
        if (sequence !== this.detailRequestSequence || !this.detailVisible) return
        if (!resp || resp.code !== 200 || !resp.data || !Array.isArray(resp.data.members)) {
          this.detailError = this.responseMessage(resp, '会话详情加载失败，请重试')
          return
        }
        this.detail = resp.data
      } catch (error) {
        if (sequence !== this.detailRequestSequence || !this.detailVisible) return
        const response = error.response
        this.detailError = this.responseMessage(response && response.data, '会话详情加载失败，请重试', response && response.status)
      } finally {
        if (sequence === this.detailRequestSequence) this.detailLoading = false
      }
    },
    responseMessage (result, fallback, httpStatus) {
      const message = result && result.message
      if (typeof message === 'string' && message.trim()) return message
      if (Number(httpStatus) === 401 || Number(result && result.code) === 401) {
        return '管理员凭证已失效或未授权（401），请重新登录'
      }
      return fallback
    }
  }
}
</script>

<style scoped>
.robot-teaching-records { padding: 20px; }
.records-card { margin-top: 20px; }
.muted { color: #606266; font-size: 13px; line-height: 1.6; }
.pager { margin-top: 16px; text-align: right; overflow-x: auto; }
.error-state { display: flex; align-items: center; gap: 16px; margin: 16px 0; color: #c45656; }
.robot-teaching-records ::v-deep .el-table .cell { white-space: normal; overflow-wrap: anywhere; word-break: break-word; }
.teaching-detail { padding: 0 24px 32px; height: 100%; box-sizing: border-box; overflow-y: auto; overflow-wrap: anywhere; word-break: break-word; }
.detail-toolbar { display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between; gap: 12px; margin-bottom: 16px; }
.session-fields { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 16px 24px; }
.session-fields > div, .member-fields > div { min-width: 0; }
dt { color: #606266; font-size: 13px; line-height: 1.6; }
dd { margin: 4px 0 0; color: #303133; line-height: 1.7; white-space: pre-wrap; }
.member-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); align-items: start; gap: 16px; }
.member-grid > .el-card { min-width: 0; }
.member-title { line-height: 1.6; font-weight: 600; white-space: pre-wrap; }
.member-fields { margin: 0; }
.member-fields > div + div { margin-top: 12px; }
.empty-state { padding: 28px 0; text-align: center; color: #909399; }
@media (max-width: 1400px) {
  .member-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}
@media (max-width: 900px) {
  .member-grid, .session-fields { grid-template-columns: minmax(0, 1fr); }
}
</style>
